import React, { useEffect, useMemo, useState } from 'react';
import { Button, Select, message } from 'antd';
import cx from 'classnames';
import ChatInput, { ChatInputValue } from 'components/ChatInput';
import { gpSearchPatientRecipient, gpSearchTeamMemberRecipient, gpSendMessage } from 'api/group';
import { sendMessage } from 'api/inbox';
import useCurrentUser from 'hooks/useCurrentUser';
import useMyAllProspectList from 'hooks/useMyAllProspectList';
import { getUserId } from 'utils/localstore';
import { EAIEditorType } from 'types/ai';
import { ERoleCode } from 'types/enumItems';
import type { TTeamMemberDto } from 'types/group';
import type { TGPRecipientPatient } from 'types/inbox';
import type { Prospect } from 'types/common';
import s from './s.module.less';

type SendToType = 'patient' | 'team';

// 固定收件人（如 client detail 的 eligibility Message patient）：隐藏 Send to / Recipient 选择
export type TFixedRecipient = {
    label: string;
    // 非 group 语境（solo）：prospect.id，走 api/inbox sendMessage
    prospectId?: number;
    // group-practice detail 路由：identityId，走 gpSendMessage GP_PROSPECT_IDENTITY
    identityId?: number;
};

type Props = {
    open: boolean;
    onClose: () => void;
    // 打开时预填的正文（可编辑）；重新打开会重置回该值
    prefillMessage?: string;
    // 打开时选中的 Send to tab（缺省 patient，与 Messages 页一致）
    initialSendTo?: SendToType;
    // team 收件人角色白名单；缺省列全部团队成员（自己除外）
    recipientRoles?: ERoleCode[];
    // 锁定收件人：不显示选择器，发送链路按 identityId/prospectId + 当前账号是否 group 分流
    fixedRecipient?: TFixedRecipient;
    onSent?: () => void;
};

// 右下角滑入的 New message 面板：样式/内容/发送链路对齐 Messages 页（InboxPage/MessageContainer）
// 内嵌的同名面板——那份长在 1700 行页面组件里无法复用，这里抽成独立组件供其他页面调用
// （如 Insurance billing 的 "Message these providers"）。角色分支同源：
// group owner/provider 的 patient 用自己的 client 列表，staff 用 GP 患者搜索；
// team 收件人来自 gp/recipient/team-member；发送 gpSendMessage（team=GP_USER / patient=PATIENT）
export default function NewMessagePanel({
    open,
    onClose,
    prefillMessage,
    initialSendTo,
    recipientRoles,
    fixedRecipient,
    onSent,
}: Props) {
    const [currentUser] = useCurrentUser();
    const [allClient] = useMyAllProspectList();
    const [sendToType, setSendToType] = useState<SendToType>(initialSendTo || 'patient');
    const [targetId, setTargetId] = useState<number | undefined>(undefined);
    const [content, setContent] = useState<ChatInputValue>({ text: '', files: [] });
    const [sending, setSending] = useState(false);
    const [teamMembers, setTeamMembers] = useState<TTeamMemberDto[]>([]);
    const [gpPatients, setGPPatients] = useState<TGPRecipientPatient[]>([]);

    // 角色分支与 MessageContainer 的 New message 逻辑一致（无 inbox 的 all-messages 场景，恒为 my 口径）
    const isGroupOwner = currentUser && currentUser.roleCode === ERoleCode.GROUP_OWNER && currentUser.groupPractice;
    const isStaff =
        currentUser &&
        [
            ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT,
            ERoleCode.GROUP_CLINICAL_ASSISTANT,
            ERoleCode.GROUP_PRACTICE_MANAGER,
        ].includes(currentUser.roleCode as ERoleCode);
    const isProviderWithGroup =
        currentUser && currentUser.roleCode === ERoleCode.GROUP_PROVIDER && currentUser.groupPractice;
    const showTeamTab = !!(isGroupOwner || isStaff || isProviderWithGroup);
    const useGPPatientSearch = !!isStaff;
    const isActingMyself = currentUser?.actingMode === 'SELF';
    const disabledSendToPatient = !isActingMyself && currentUser?.roleCode === ERoleCode.GROUP_OWNER;
    const showPatientTab = !disabledSendToPatient;
    const showSendToToggle = showPatientTab && showTeamTab;

    useEffect(() => {
        if (!open) return;
        // 每次打开重置：tab 回到初始、收件人清空、正文回到预填值
        setSendToType(initialSendTo && (initialSendTo !== 'patient' || showPatientTab) ? initialSendTo : 'patient');
        setTargetId(undefined);
        setContent({ text: prefillMessage || '', files: [] });
        let cancelled = false;
        // 固定收件人模式不需要拉收件人列表
        if (fixedRecipient) {
            // eslint-disable-next-line consistent-return
            return () => {
                cancelled = true;
            };
        }
        if (showTeamTab) {
            (async () => {
                const res: any = await gpSearchTeamMemberRecipient('');
                if (!cancelled) setTeamMembers((!res?.error && res?.data?.data) || []);
            })();
        }
        if (useGPPatientSearch) {
            (async () => {
                const res: any = await gpSearchPatientRecipient('');
                if (!cancelled) setGPPatients((!res?.error && res?.data?.data) || []);
            })();
        }
        // eslint-disable-next-line consistent-return
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const clientOption = useMemo(() => allClient?.filter((item: Prospect) => !item.isSample), [allClient]);

    const teamOptions = useMemo(
        () =>
            teamMembers
                .filter((m) => m.userId !== currentUser?.user?.id)
                .filter((m) => !recipientRoles || recipientRoles.includes(m.roleCode)),
        [teamMembers, currentUser, recipientRoles],
    );

    const handleDiscard = () => {
        setTargetId(undefined);
        setContent({ text: '', files: [] });
    };

    const handleClose = () => {
        handleDiscard();
        onClose();
    };

    const handleSend = async () => {
        const fixedTargetId = fixedRecipient ? (fixedRecipient.identityId ?? fixedRecipient.prospectId) : undefined;
        const effectiveTargetId = fixedRecipient ? fixedTargetId : targetId;
        if (!effectiveTargetId || (!content.text.trim() && content.files.length === 0)) return;
        const msg = content.text.trim() ? content.text.replace(/<[^>]+>/g, '') : '';
        // useGPPatientSearch 时下拉 value 是 identityId，须用 GP_PROSPECT_IDENTITY；否则是 prospect.id
        const recipientType =
            // eslint-disable-next-line no-nested-ternary
            sendToType === 'team' ? 'GP_USER' : useGPPatientSearch ? 'GP_PROSPECT_IDENTITY' : 'PATIENT';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let doSend: (msgText?: string, fileId?: number) => Promise<any>;
        if (fixedRecipient) {
            // 固定收件人：group 账号走 gpSendMessage（detail 的 GP 路由给 identityId），solo 走 inbox sendMessage
            if (currentUser?.groupPractice) {
                const fixedType = fixedRecipient.identityId ? 'GP_PROSPECT_IDENTITY' : 'PATIENT';
                doSend = (msgText?: string, fileId?: number) =>
                    gpSendMessage({ recipientType: fixedType, targetId: effectiveTargetId, message: msgText, fileId });
            } else {
                doSend = (msgText?: string, fileId?: number) =>
                    sendMessage({ providerId: getUserId()!, clientId: effectiveTargetId, message: msgText, fileId });
            }
        } else {
            doSend = (msgText?: string, fileId?: number) =>
                gpSendMessage({ recipientType, targetId: targetId!, message: msgText, fileId });
        }
        setSending(true);
        try {
            if (msg) {
                const res: any = await doSend(msg);
                if (res?.error) return;
            }
            if (content.files.length > 0) {
                await Promise.all(content.files.map((file) => doSend(undefined, file.id)));
            }
            message.success('Sent successfully!');
            onSent?.();
            handleClose();
        } finally {
            setSending(false);
        }
    };

    // 角色展示（同 MessageContainer 收件人下拉）
    const roleMeta = (roleCode: ERoleCode) => {
        switch (roleCode) {
            case ERoleCode.GROUP_OWNER:
                return { name: 'Owner', sub: 'Practice owner', cls: 'owner', avatar: '#0e62f4' };
            case ERoleCode.GROUP_PROVIDER:
                return { name: 'Provider', sub: 'Provider', cls: 'provider', avatar: '#0e62f4' };
            case ERoleCode.GROUP_PRACTICE_MANAGER:
                return {
                    name: 'Practice manager',
                    sub: 'Operations & team workflows',
                    cls: 'practiceMgr',
                    avatar: '#92400e',
                };
            case ERoleCode.GROUP_ADMINISTRATIVE_ASSISTANT:
                return {
                    name: 'Admin assistant',
                    sub: 'Scheduling & patient communication',
                    cls: 'adminAsst',
                    avatar: '#1e40af',
                };
            case ERoleCode.GROUP_CLINICAL_ASSISTANT:
                return {
                    name: 'Clinical assistant',
                    sub: 'Labs & prescriptions support',
                    cls: 'clinicalAsst',
                    avatar: '#065f46',
                };
            default:
                return { name: 'Staff', sub: '', cls: 'staff', avatar: '#6b7280' };
        }
    };

    return (
        <div className={cx(s.newMsgWrap, open && s.newMsgWrapShow)}>
            <div className={s.nTitle}>
                <span>New message</span>
                <svg
                    onClick={handleClose}
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                >
                    <path
                        d="M6 18L18 6M6 6L18 18"
                        stroke="#4B5563"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>
            <div className={s.nContent}>
                {showSendToToggle && !fixedRecipient && (
                    <>
                        <div className={s.nLabel}>Send to</div>
                        <div className={s.recipientToggle}>
                            <Button
                                className={cx(
                                    s.recipientToggleBtn,
                                    sendToType === 'patient' && s.recipientToggleBtnActive,
                                )}
                                onClick={() => {
                                    setSendToType('patient');
                                    setTargetId(undefined);
                                }}
                            >
                                Patient
                            </Button>
                            <Button
                                className={cx(
                                    s.recipientToggleBtn,
                                    sendToType === 'team' && s.recipientToggleBtnActive,
                                )}
                                onClick={() => {
                                    setSendToType('team');
                                    setTargetId(undefined);
                                }}
                            >
                                Team member
                            </Button>
                        </div>
                    </>
                )}
                <div className={s.nLabel}>Recipient</div>
                {fixedRecipient && <div className={s.fixedRecipient}>{fixedRecipient.label}</div>}
                {!fixedRecipient && sendToType === 'patient' && showPatientTab && useGPPatientSearch && (
                    <Select
                        showSearch
                        optionLabelProp="label"
                        className={s.select}
                        onChange={(val: number) => setTargetId(val)}
                        filterOption={(input, option) => {
                            const name = String(option?.['data-name'] || '');
                            return name.toLowerCase().includes(input.toLowerCase());
                        }}
                        value={targetId}
                        placeholder="Search patient..."
                    >
                        {gpPatients.map((item) => {
                            const fullName = `${item.firstName} ${item.lastName}`;
                            return (
                                <Select.Option
                                    value={item.identityId}
                                    key={item.identityId}
                                    label={fullName}
                                    data-name={`${fullName} ${item.providerName || ''}`}
                                >
                                    {fullName}
                                    {item.providerName && <span className={s.optProvider}>({item.providerName})</span>}
                                </Select.Option>
                            );
                        })}
                    </Select>
                )}
                {!fixedRecipient && sendToType === 'patient' && showPatientTab && !useGPPatientSearch && (
                    <Select
                        showSearch
                        className={s.select}
                        onChange={(val: number) => setTargetId(val)}
                        filterOption={(input, option) => {
                            const name = option?.children?.join('');
                            if (!name) return true;
                            return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        value={targetId}
                        placeholder="Select recipient..."
                    >
                        {clientOption?.map((item: Prospect) => (
                            <Select.Option value={item.id} key={item.id}>
                                {item.firstName} {item.lastName}
                            </Select.Option>
                        ))}
                    </Select>
                )}
                {!fixedRecipient && sendToType === 'team' && showTeamTab && (
                    <Select
                        showSearch
                        className={s.select}
                        optionLabelProp="label"
                        onChange={(val: number) => setTargetId(val)}
                        filterOption={(input, option) => {
                            const name = option?.['data-name'] as string;
                            if (!name) return true;
                            return name.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        value={targetId}
                        placeholder="Select recipient..."
                    >
                        {teamOptions.map((member) => {
                            const fullName = `${member.firstName} ${member.lastName}`;
                            const initials =
                                `${(member.firstName || '')[0] || ''}${(member.lastName || '')[0] || ''}`.toUpperCase();
                            const meta = roleMeta(member.roleCode);
                            return (
                                <Select.Option
                                    value={member.userId}
                                    key={member.userId}
                                    label={fullName}
                                    data-name={fullName}
                                >
                                    <div className={s.optRow}>
                                        <span className={s.optAvatar} style={{ background: meta.avatar }}>
                                            {initials}
                                        </span>
                                        <span className={s.optMain}>
                                            <span className={s.optName}>
                                                {fullName}
                                                <span className={cx(s.roleTag, s[meta.cls])}>{meta.name}</span>
                                            </span>
                                            {meta.sub && <span className={s.optSub}>{meta.sub}</span>}
                                        </span>
                                    </div>
                                </Select.Option>
                            );
                        })}
                    </Select>
                )}
                <div className={s.nLabel}>Message</div>
                <div className={s.inputWrap}>
                    <ChatInput
                        value={content}
                        onChange={(value) => setContent(value)}
                        type={EAIEditorType.PROSPECT}
                        clientId={
                            (fixedRecipient ? (fixedRecipient.prospectId ?? fixedRecipient.identityId) : targetId)!
                        }
                        hideSend
                    />
                </div>
            </div>
            <div className={s.nFooter}>
                <Button className={s.discardBtn} onClick={handleDiscard}>
                    Discard
                </Button>
                <Button
                    loading={sending}
                    disabled={(!fixedRecipient && !targetId) || (!content.text.trim() && content.files.length === 0)}
                    type="primary"
                    onClick={handleSend}
                >
                    Send
                </Button>
            </div>
        </div>
    );
}
